// documentStatusStore.ts -- registry for document status windows.
//
// Workbench I18 (C.7). Tracks which document IDs have an open status
// window AND whether each window is currently minimized. The actual
// status data (stage progress, durations, etc.) lives inside the
// DocumentStatusWindow component as React state -- it polls the
// server every ~30s while open and the digest is in flight.
//
// Persistence: open windows persist to localStorage so a page reload
// keeps in-flight status windows visible. (User direction: "a user
// can minimize it to a pill or close it and re-open it with the
// status by clicking on the file.")
import { useMemo } from 'react';
import { create } from 'zustand';
const STORAGE_KEY = 'fdn:doc-status-windows';
/** Composite key: `${kind}:${documentId}`. Lets the same documentId
 *  exist as both a panel row and a floating window without collision. */
function entryKey(kind, documentId) {
    return `${kind}:${documentId}`;
}
function loadRegistry() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw)
            return { windows: {}, windowOrder: [] };
        const parsed = JSON.parse(raw);
        if (!parsed.windows || !Array.isArray(parsed.windowOrder)) {
            return { windows: {}, windowOrder: [] };
        }
        return parsed;
    }
    catch {
        return { windows: {}, windowOrder: [] };
    }
}
function persistRegistry(windows, windowOrder, panelDetached, panelPose) {
    try {
        const payload = { windows, windowOrder, panelDetached, panelPose };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    }
    catch {
        // best-effort -- localStorage might be full or disabled in private browsing
    }
}
export const useDocumentStatusStore = create((set, get) => {
    const initial = loadRegistry();
    // Migration: any persisted entries from before the kind/key rewrite
    // were stored under bare documentId keys without a `kind` field.
    // Promote them to `floating:<id>` (the prior single-kind behaviour).
    const migratedWindows = {};
    const migratedOrder = [];
    for (const oldKey of initial.windowOrder) {
        const entry = initial.windows[oldKey];
        if (!entry)
            continue;
        if ('kind' in entry && (entry.kind === 'panel' || entry.kind === 'floating')) {
            migratedWindows[oldKey] = entry;
            migratedOrder.push(oldKey);
        }
        else {
            const docId = entry.documentId ?? oldKey;
            const newKey = entryKey('floating', docId);
            migratedWindows[newKey] = {
                ...entry,
                kind: 'floating',
                documentId: docId,
            };
            migratedOrder.push(newKey);
        }
    }
    // Helper: persist the current store state to localStorage. Uses
    // `get()` to snapshot the panel detach + pose so callers don't have
    // to thread them through. Cheap on every change.
    function persist(nextWindows, nextOrder) {
        const s = get();
        persistRegistry(nextWindows, nextOrder, s.panelDetached, s.panelPose);
    }
    return {
        windows: migratedWindows,
        windowOrder: migratedOrder,
        panelDetached: initial.panelDetached ?? false,
        panelPose: initial.panelPose ?? { x: 200, y: 80 },
        openWindow: (documentId, filename, kind = 'panel') => {
            const key = entryKey(kind, documentId);
            const existing = get().windows[key];
            if (existing) {
                if (kind === 'floating' && existing.minimized) {
                    get().setMinimized(documentId, false);
                }
                else if (kind === 'panel' && !existing.expandedInPanel) {
                    get().setExpandedInPanel(documentId, true);
                }
                return;
            }
            const entry = {
                kind,
                documentId,
                ...(filename ? { filename } : {}),
                minimized: false,
                openedAt: Date.now(),
                ...(kind === 'panel' ? { expandedInPanel: false } : {}),
            };
            set((s) => {
                const nextWindows = { ...s.windows, [key]: entry };
                const nextOrder = [...s.windowOrder, key];
                persist(nextWindows, nextOrder);
                return { windows: nextWindows, windowOrder: nextOrder };
            });
        },
        closeWindow: (documentId, kind) => {
            const key = entryKey(kind, documentId);
            set((s) => {
                if (!s.windows[key])
                    return s;
                const { [key]: _, ...nextWindows } = s.windows;
                const nextOrder = s.windowOrder.filter((k) => k !== key);
                persist(nextWindows, nextOrder);
                return { windows: nextWindows, windowOrder: nextOrder };
            });
        },
        setMinimized: (documentId, minimized) => {
            const key = entryKey('floating', documentId);
            set((s) => {
                const w = s.windows[key];
                if (!w || w.minimized === minimized)
                    return s;
                const nextWindows = { ...s.windows, [key]: { ...w, minimized } };
                persist(nextWindows, s.windowOrder);
                return { windows: nextWindows };
            });
        },
        setPose: (documentId, pose) => {
            const key = entryKey('floating', documentId);
            set((s) => {
                const w = s.windows[key];
                if (!w)
                    return s;
                const nextWindows = { ...s.windows, [key]: { ...w, pose } };
                persist(nextWindows, s.windowOrder);
                return { windows: nextWindows };
            });
        },
        setExpandedInPanel: (documentId, expanded) => {
            const key = entryKey('panel', documentId);
            set((s) => {
                const w = s.windows[key];
                if (!w)
                    return s;
                const nextWindows = {
                    ...s.windows,
                    [key]: { ...w, expandedInPanel: expanded },
                };
                persist(nextWindows, s.windowOrder);
                return { windows: nextWindows };
            });
        },
        setPanelDetached: (detached) => {
            set((s) => {
                if (s.panelDetached === detached)
                    return s;
                // Persist directly here -- don't go through `persist()` because
                // that helper reads the OLD detach value via get(). We've
                // updated set() with the new value; localStorage needs to
                // match.
                persistRegistry(s.windows, s.windowOrder, detached, s.panelPose);
                return { panelDetached: detached };
            });
        },
        setPanelPose: (pose) => {
            set((s) => {
                persistRegistry(s.windows, s.windowOrder, s.panelDetached, pose);
                return { panelPose: pose };
            });
        },
    };
});
/** Hook: list of all entry keys (`${kind}:${documentId}`) in insertion
 *  order. Hosts can filter by kind. */
export function useDocumentStatusIds() {
    return useDocumentStatusStore((s) => s.windowOrder);
}
/** Hook: list of FLOATING entry documentIds in insertion order. Used
 *  by DocumentStatusWindowsHost.
 *
 *  Implementation note: zustand's selector contract requires the
 *  selector to return a STABLE reference when the store hasn't
 *  changed in a way that affects the result. Building a new array
 *  inside the selector returns a fresh reference every render and
 *  triggers infinite re-renders. We work around this by selecting
 *  the raw windowOrder + windows slices and deriving the filtered
 *  list with useMemo at the call-site level. */
export function useFloatingStatusIds() {
    const order = useDocumentStatusStore((s) => s.windowOrder);
    const windows = useDocumentStatusStore((s) => s.windows);
    return useMemo(() => {
        const out = [];
        for (const key of order) {
            const w = windows[key];
            if (w?.kind === 'floating')
                out.push(w.documentId);
        }
        return out;
    }, [order, windows]);
}
/** Hook: list of PANEL entry documentIds in NEWEST-FIRST order. Used
 *  by the top-bar Digest Status dropdown. Same memoization rationale
 *  as useFloatingStatusIds. */
export function usePanelStatusIds() {
    const order = useDocumentStatusStore((s) => s.windowOrder);
    const windows = useDocumentStatusStore((s) => s.windows);
    return useMemo(() => {
        const panels = [];
        for (const key of order) {
            const w = windows[key];
            if (w?.kind === 'panel')
                panels.push({ documentId: w.documentId, openedAt: w.openedAt });
        }
        panels.sort((a, b) => b.openedAt - a.openedAt);
        return panels.map((p) => p.documentId);
    }, [order, windows]);
}
/** Hook: a single FLOATING entry (or undefined). The kind is implicit
 *  -- this hook is used by the floating window component. */
export function useDocumentStatusWindow(documentId) {
    return useDocumentStatusStore((s) => s.windows[entryKey('floating', documentId)]);
}
/** Hook: a single PANEL entry (or undefined). Used by the panel-row
 *  component. */
export function usePanelStatusEntry(documentId) {
    return useDocumentStatusStore((s) => s.windows[entryKey('panel', documentId)]);
}
